import * as XLSX from "xlsx";

const normalizeHeader = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[._-]+/g, " ")
    .replace(/\s+/g, " ");

const firstDefined = (row, normalizedRow, aliases) => {
  for (const alias of aliases) {
    const direct = row[alias];
    if (direct !== undefined && direct !== null && String(direct).trim() !== "") {
      return direct;
    }

    const normalized = normalizedRow[normalizeHeader(alias)];
    if (normalized !== undefined && normalized !== null && String(normalized).trim() !== "") {
      return normalized;
    }
  }
  return null;
};

const toNullableNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const toNullableString = (value) => {
  if (value === null || value === undefined) return null;
  const stringValue = String(value).trim();
  return stringValue === "" ? null : stringValue;
};

const QUESTION_ID_HEADERS = [
  "qid",
  "q id",
  "question id",
  "question_id",
  "Question ID",
];

const FIELD_HEADERS = {
  display_number: ["Q.number", "Q number", "Q. number", "question number", "display number"],
  sub_topics: ["sub Topic", "sub topic", "sub_topic", "sub topics"],
  section: ["Section", "section"],
  section_type: ["Type", "section type", "section_type"],
  examName: ["Exam Name", "exam name", "exam_name"],
  examYear: ["Exam Year", "exam year", "exam_year"],
  seq: ["order", "seq", "sequence"],
  correct: ["correct_score", "correct score", "correct"],
  incorrect: ["incorrect_score", "incorrect score", "incorrect"],
  unattempted: ["unattempt_score", "unattempt score", "unattempted", "unattempted score"],
  average: ["average", "average_score", "average score"],
};

const isMissing = (value) =>
  value === null || value === undefined || (typeof value === "string" && value.trim() === "");

const normalizeRow = (row) =>
  Object.entries(row).reduce((acc, [key, value]) => {
    acc[normalizeHeader(key)] = value;
    return acc;
  }, {});

export const readModuleMetadataFromExcel = async (file) => {
  if (!file) return new Map();

  const data = await file.arrayBuffer();
  const workbook = XLSX.read(data, { type: "array" });
  const metadataByQid = new Map();
  let excelRowOrder = 0;

  workbook.SheetNames.forEach((sheetName) => {
    // Section-like cells in the template are intentionally sparse. Carry their
    // last explicit value forward within a sheet, matching the renderer's
    // existing persistence without allowing metadata to bleed across sheets.
    const persistent = {
      sub_topics: null,
      section: null,
      section_type: null,
      examName: null,
      examYear: null,
      seq: null,
    };

    const sheet = workbook.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json(sheet, { defval: null, raw: false });

    rows.forEach((row) => {
      const normalizedRow = normalizeRow(row);
      const qid = toNullableString(firstDefined(row, normalizedRow, QUESTION_ID_HEADERS));
      if (!qid) return;

      excelRowOrder += 1;

      const rawMetadata = {};
      Object.entries(FIELD_HEADERS).forEach(([field, aliases]) => {
        rawMetadata[field] = firstDefined(row, normalizedRow, aliases);
      });

      ["sub_topics", "section", "section_type", "examName", "examYear", "seq"].forEach((field) => {
        if (!isMissing(rawMetadata[field])) {
          persistent[field] = rawMetadata[field];
        }
      });

      metadataByQid.set(String(qid), {
        display_number: toNullableNumber(rawMetadata.display_number),
        sub_topics: toNullableString(persistent.sub_topics),
        section: toNullableString(persistent.section),
        section_type: toNullableString(persistent.section_type),
        examName: toNullableString(persistent.examName),
        examYear: toNullableString(persistent.examYear),
        seq: toNullableNumber(persistent.seq),
        correct: toNullableNumber(rawMetadata.correct),
        incorrect: toNullableNumber(rawMetadata.incorrect),
        unattempted: toNullableNumber(rawMetadata.unattempted),
        average: toNullableNumber(rawMetadata.average),
        _moduleExcelRowOrder: excelRowOrder,
      });
    });
  });

  return metadataByQid;
};

export const mergeModuleMetadata = (questions, metadataByQid) => {
  if (!Array.isArray(questions) || !(metadataByQid instanceof Map)) {
    return Array.isArray(questions) ? questions : [];
  }

  return questions
    .map((question, originalIndex) => {
      const qid = question?.qid ?? question?.id ?? question?.question_id;
      const merged = { ...question, _moduleOriginalOrder: originalIndex };
      if (qid === null || qid === undefined) return merged;

      const excelMetadata = metadataByQid.get(String(qid));
      if (!excelMetadata) return merged;

      Object.entries(excelMetadata).forEach(([field, value]) => {
        // Never overwrite a non-empty API value. This makes the workaround
        // compatible with a future backend fix. Internal Excel row order is
        // absent from the API and is therefore added as a stable sort key.
        if (isMissing(merged[field]) && !isMissing(value)) {
          merged[field] = value;
        }
      });

      return merged;
    })
    .sort((a, b) => {
      const aExcelOrder = Number(a._moduleExcelRowOrder);
      const bExcelOrder = Number(b._moduleExcelRowOrder);
      const aHasExcelOrder = Number.isFinite(aExcelOrder);
      const bHasExcelOrder = Number.isFinite(bExcelOrder);

      if (aHasExcelOrder && bHasExcelOrder) return aExcelOrder - bExcelOrder;
      if (aHasExcelOrder) return -1;
      if (bHasExcelOrder) return 1;
      return a._moduleOriginalOrder - b._moduleOriginalOrder;
    })
    .map(({ _moduleOriginalOrder, ...question }) => question);
};
